"use client"

/**
 * Shared form-submission helper used by every form on the site.
 *
 * The browser POSTs to a same-origin Next.js API route at /api/forms/submit,
 * which then forwards the payload to the workflow webhook with
 * Content-Type: application/json (a header that cross-origin no-cors
 * fetches silently strip). This keeps the workflow's webhook parser happy
 * without any CORS configuration.
 *
 * Per-source env vars (set in project's Secrets Manager):
 *   NEXT_PUBLIC_FORM_WEBHOOK_URL_CONTACT
 *   NEXT_PUBLIC_FORM_WEBHOOK_URL_VOLUNTEER
 *   NEXT_PUBLIC_FORM_WEBHOOK_URL_BOOKING
 *   NEXT_PUBLIC_FORM_WEBHOOK_URL_SERVICE
 *   NEXT_PUBLIC_FORM_WEBHOOK_URL_YAP_CONNECTORS
 *   NEXT_PUBLIC_FORM_WEBHOOK_URL_HUB_SUBSCRIBE
 *   NEXT_PUBLIC_FORM_WEBHOOK_URL_NEWSLETTER
 *
 * Legacy single-URL fallback (used when a per-source var is not set):
 *   NEXT_PUBLIC_FORM_WEBHOOK_URL
 *
 * The env vars are read on the SERVER in the API route, not here — the
 * client only needs to know the relative path. Keep this file
 * environment-agnostic so call sites don't drift.
 */

export type SubmissionSource =
  | "contact"
  | "volunteer"
  | "booking"
  | "service"
  | "newsletter"
  | "yap-connectors"
  | "hub-subscribe"

type Payload = Record<string, string>

/**
 * Persist a submission locally as a fallback / audit trail.
 * Keyed per-source so different forms don't clobber each other.
 *
 * Only used when the server route can't be reached at all (offline, server
 * down). When the route is hit, the server is responsible for forwarding.
 */
function storeLocally(source: SubmissionSource, data: Payload) {
  try {
    const key = `bullytalk:submissions:${source}`
    const existing = JSON.parse(localStorage.getItem(key) || "[]")
    existing.push({
      submittedAt: new Date().toISOString(),
      data,
    })
    localStorage.setItem(key, JSON.stringify(existing.slice(-50)))
  } catch {
    // localStorage may be unavailable (private mode, quota). Non-fatal.
  }
}

/**
 * Pull the Cloudflare Turnstile token off the form. Cloudflare's widget
 * auto-injects a hidden input named `cf-turnstile-response` whenever a token
 * is issued. If there happen to be multiple inputs sharing that name (e.g. a
 * stray duplicate), pick the one that actually has a value rather than
 * relying on RadioNodeList ordering.
 */
function readTurnstileToken(form: HTMLFormElement): string {
  const candidates = Array.from(
    form.querySelectorAll<HTMLInputElement>('input[name="cf-turnstile-response"]'),
  )
  for (const el of candidates) {
    const v = (el.value || "").trim()
    if (v) return v
  }
  return ""
}

/**
 * Submit a form payload and route to the thank-you page.
 *
 * - Calls `onSubmit` with the parsed FormData so callers can clear fields,
 *   flip a "submitted" flag, etc.
 * - POSTs to the same-origin /api/forms/submit route, which forwards to
 *   the configured webhook. `keepalive: true` so the in-flight fetch
 *   survives the redirect to /thank-you.
 * - Always redirects to /thank-you?from=<source> so the user sees confirmation.
 */
export async function submitForm(
  source: SubmissionSource,
  form: HTMLFormElement,
  options?: { onSubmit?: (data: Payload) => void; redirect?: string | false }
): Promise<void> {
  const formData = new FormData(form)
  const data: Payload = {}
  formData.forEach((value, key) => {
    data[key] = typeof value === "string" ? value : String(value)
  })

  options?.onSubmit?.(data)

  // Pull the Turnstile token off the form (hidden input managed by
  // <TurnstileGate/>). Forwarded alongside the payload so the server can
  // verify it against Cloudflare's siteverify endpoint.
  const turnstileToken = readTurnstileToken(form)

  // Fire-and-forget; the thank-you page is the source of truth for the user.
  // Same-origin means no CORS, full Content-Type: application/json, and the
  // server can return a real status code we can react to. keepalive lets the
  // request complete even though we're about to navigate away.
  fetch("/api/forms/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      source,
      data,
      submittedAt: new Date().toISOString(),
      turnstileToken,
    }),
    keepalive: true,
  }).catch(() => {
    /* network errors are non-fatal — fall back to local storage */
    storeLocally(source, data)
  })

  if (options?.redirect === false) return
  const dest = options?.redirect ?? "/thank-you"
  const sep = dest.includes("?") ? "&" : "?"
  window.location.href = `${dest}${sep}from=${encodeURIComponent(source)}`
}

/**
 * Block the default submit + run the shared handler. Convenience wrapper so
 * callers don't repeat the preventDefault dance.
 *
 * The submit button is expected to be `disabled` until <TurnstileGate/> issues
 * a token — but we still guard here as belt-and-suspenders in case a form
 * forgets to wire the disabled state.
 */
export function handleFormSubmit(
  source: SubmissionSource,
  options?: Parameters<typeof submitForm>[2]
) {
  return async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    // Native required-validation runs before submit fires; guard anyway.
    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }
    // Block submission when the visitor hasn't passed the Turnstile challenge.
    // The server will also reject, but failing here gives instant feedback.
    if (!readTurnstileToken(form)) {
      // eslint-disable-next-line no-alert
      alert(
        "Please complete the spam check (Cloudflare Turnstile) before submitting.",
      )
      return
    }
    await submitForm(source, form, options)
  }
}
